import { useState, useMemo } from 'react'
import type { ProyectoPlan } from './tipos'
import {
  IconoGrafico,
  IconoCartera,
  IconoReloj,
  IconoCaja,
  IconoCheck,
  IconoAlerta,
  IconoEditar,
} from '@/shared/ui/Iconos/Iconos'
import { CampoPesos } from './CampoPesos'
import { useActasCorte } from './cortes/useActasCorte'
import estilos from './CurvaSProyectos.module.css'

function formatearMoneda(monto: number) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(monto)
}

function diasEntre(f1: string, f2: string) {
  const d1 = new Date(f1).getTime()
  const d2 = new Date(f2).getTime()
  return Math.round((d2 - d1) / (1000 * 60 * 60 * 24))
}

type PropsCurvaS = {
  proyecto: ProyectoPlan
  onActualizarProyecto?: (cambios: Partial<ProyectoPlan>) => void
}

export function CurvaSProyectos({ proyecto, onActualizarProyecto }: PropsCurvaS) {
  const [editandoTope, setEditandoTope] = useState(false)
  const [topeTemp, setTopeTemp] = useState(proyecto.tope_contrato || proyecto.presupuesto)
  const [fechaVenceTemp, setFechaVenceTemp] = useState(proyecto.fecha_vencimiento_contrato || proyecto.fecha_fin)
  const [alertaEnviada, setAlertaEnviada] = useState(false)
  const { actas } = useActasCorte(proyecto.id)

  // Tarea 2.10: Total cobrado en caja (actas cobradas)
  const valorCobradoTotal = useMemo(() => {
    return actas
      .filter((a) => a.estado === 'cobrada')
      .reduce((sum, a) => sum + Number(a.valor_total || 0), 0)
  }, [actas])

  const pctCobradoPonderado = useMemo(() => {
    const base = proyecto.presupuesto || 1
    return Math.min(100, Math.round((valorCobradoTotal / base) * 100))
  }, [valorCobradoTotal, proyecto.presupuesto])


  // 1. Cálculos Contractuales
  const topeContrato = proyecto.tope_contrato || proyecto.presupuesto
  const presupuestoObra = proyecto.presupuesto

  // Avance real consolidado y valor devengado
  const { totalModulos, avancePonderado, valorDevengado, horasProgramadasTotales, horasEjecutadasTotales, costoDespachadoTotal, faltantesStock } = useMemo(() => {
    let mods = 0
    let sumaPonderada = 0
    let sumaDevengada = 0
    let horasProg = 0
    let horasEjec = 0
    let despachado = 0
    const faltantes: Array<{
      moduloCodigo: string
      moduloNombre: string
      material: string
      requerido: number
      unidad: string
    }> = []

    for (const etapa of proyecto.etapas || []) {
      for (const mod of etapa.modulos || []) {
        mods++
        const av = mod.avance || 0
        const val = mod.valor_total || 0
        sumaPonderada += (av * val)
        sumaDevengada += (val * av) / 100

        const hProg = Number(mod.horas_programadas) > 0 ? Number(mod.horas_programadas) : (Number(mod.horas) || 0)
        const hEjec = Number(mod.horas_ejecutadas) > 0 ? Number(mod.horas_ejecutadas) : (Number(mod.horas_reales) || 0)
        horasProg += hProg
        horasEjec += hEjec

        despachado += (mod.costo_materiales_despachados || 0)

        // Evaluar insumos si no están despachados
        if (mod.alistamiento_estado !== 'despachado' && Array.isArray(mod.elementos)) {
          for (const el of mod.elementos) {
            if (el.tipo === 'material' || !el.tipo) {
              // Si requiere material y no está despachado, se considera faltante para alistamiento
              faltantes.push({
                moduloCodigo: mod.codigo,
                moduloNombre: mod.nombre,
                material: el.descripcion,
                requerido: el.cantidad,
                unidad: el.unidad,
              })
            }
          }
        }
      }
    }

    const pctReal = presupuestoObra > 0 ? (sumaPonderada / presupuestoObra) : 0
    return {
      totalModulos: mods,
      avancePonderado: Math.round(pctReal * 10) / 10,
      valorDevengado: Math.round(sumaDevengada),
      horasProgramadasTotales: Math.round(horasProg),
      horasEjecutadasTotales: Math.round(horasEjec),
      costoDespachadoTotal: Math.round(despachado),
      faltantesStock: faltantes,
    }
  }, [proyecto, presupuestoObra])

  const desfaseLiquidez = Math.max(0, valorDevengado - valorCobradoTotal)
  const saldoContractual = topeContrato - valorDevengado
  const pctConsumoTope = topeContrato > 0 ? Math.round((valorDevengado / topeContrato) * 100) : 0

  // Tiempos contractuales
  const hoyStr = new Date().toISOString().split('T')[0]
  const diasTotalesContrato = Math.max(1, diasEntre(proyecto.fecha_inicio, proyecto.fecha_vencimiento_contrato || proyecto.fecha_fin))
  const diasTranscurridos = Math.max(0, diasEntre(proyecto.fecha_inicio, hoyStr))
  const diasRestantes = Math.max(0, diasEntre(hoyStr, proyecto.fecha_vencimiento_contrato || proyecto.fecha_fin))
  const pctTiempoTranscurrido = Math.min(100, Math.round((diasTranscurridos / diasTotalesContrato) * 100))

  // Índice de Desempeño del Cronograma (SPI = Avance Real / Tiempo Transcurrido)
  const spi = pctTiempoTranscurrido > 0 ? (avancePonderado / pctTiempoTranscurrido).toFixed(2) : '1.00'

  function handleGuardarTope() {
    if (onActualizarProyecto) {
      onActualizarProyecto({
        tope_contrato: topeTemp,
        fecha_vencimiento_contrato: fechaVenceTemp,
      })
    }
    setEditandoTope(false)
  }

  function handleNotificarBodega() {
    setAlertaEnviada(true)
    setTimeout(() => setAlertaEnviada(false), 3500)
  }

  // 2. Generación de Puntos de la Curva S y Geometría SVG
  const viewBoxW = 800
  const viewBoxH = 285
  const paddingX = 55
  const paddingY = 32
  const chartW = viewBoxW - paddingX * 2
  const chartH = 205 // Deja 48px libres abajo para ticks y título del eje X

  const coordX = (idx: number) => paddingX + (idx / 10) * chartW
  const coordY = (pct: number) => paddingY + chartH - (pct / 100) * chartH

  // Coordenada exacta de HOY (sin redondear a intervalos discretos)
  const tHoy = Math.min(1, Math.max(0, pctTiempoTranscurrido / 100))
  const xHoy = paddingX + tHoy * chartW

  // Función logística sigmoidal estándar para la Curva S planeada
  const sCurva = (t: number) => Math.round((1 / (1 + Math.exp(-6 * (t - 0.5)))) * 100)

  const puntosCurva = useMemo(() => {
    const totalPuntos = 10
    const puntos: Array<{
      indice: number
      etiqueta: string
      pctPlaneado: number
    }> = []

    for (let i = 0; i <= totalPuntos; i++) {
      const t = i / totalPuntos
      puntos.push({
        indice: i,
        etiqueta: `Mes ${i}`,
        pctPlaneado: Math.min(100, Math.max(0, sCurva(t))),
      })
    }
    return puntos
  }, [])

  const dPlaneada = puntosCurva
    .map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${coordX(idx)} ${coordY(p.pctPlaneado)}`)
    .join(' ')

  // Costo despachado en porcentaje de la obra
  const pctCostosActual = Math.min(100, Math.round((costoDespachadoTotal / (presupuestoObra || 1)) * 100))

  // Construcción de curvas reales continuas que terminan EXACTAMENTE en xHoy (Corte de hoy)
  const { coordsReales, coordsCostos, coordsCobrados } = useMemo(() => {
    const cReal: Array<{ x: number; y: number; pct: number }> = []
    const cCost: Array<{ x: number; y: number; pct: number }> = []
    const cCob: Array<{ x: number; y: number; pct: number }> = []

    // 1. Puntos intermedios antes de hoy
    for (let i = 0; i <= 10; i++) {
      const t = i / 10
      if (t < tHoy || (tHoy === 0 && i === 0)) {
        const factor = tHoy > 0 ? (t / tHoy) : 1
        const x = coordX(i)

        const r = Math.min(100, Math.round(avancePonderado * factor))
        cReal.push({ x, y: coordY(r), pct: r })

        const c = Math.min(100, Math.round(pctCostosActual * factor))
        cCost.push({ x, y: coordY(c), pct: c })

        const cob = Math.min(100, Math.round(pctCobradoPonderado * factor))
        cCob.push({ x, y: coordY(cob), pct: cob })
      }
    }

    // 2. Punto final exacto en la línea de HOY (xHoy)
    if (tHoy > 0) {
      cReal.push({ x: xHoy, y: coordY(avancePonderado), pct: avancePonderado })
      cCost.push({ x: xHoy, y: coordY(pctCostosActual), pct: pctCostosActual })
      cCob.push({ x: xHoy, y: coordY(pctCobradoPonderado), pct: pctCobradoPonderado })
    }

    return { coordsReales: cReal, coordsCostos: cCost, coordsCobrados: cCob }
  }, [tHoy, xHoy, avancePonderado, pctCostosActual, pctCobradoPonderado, chartW, chartH])

  const dReal = coordsReales.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const dCostos = coordsCostos.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const dCobrado = coordsCobrados.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')

  return (
    <div className={estilos.contenedorCurvaS}>
      {/* 1. KPIs de Control Contractual y Presupuestal */}
      <section className={estilos.kpisControlGrid}>
        {/* Tope Contractual */}
        <div className={estilos.kpiCard}>
          <div className={estilos.kpiTitulo}>
            <span>Tope de Contrato</span>
            {onActualizarProyecto && (
              <button
                type="button"
                onClick={() => setEditandoTope(!editandoTope)}
                style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#5E2AB8' }}
                title="Editar tope o vigencia contractual"
              >
                <IconoEditar style={{ width: '15px', height: '15px' }} />
              </button>
            )}
          </div>
          {editandoTope ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <CampoPesos
                valor={topeTemp}
                onCambio={(v) => setTopeTemp(v)}
                style={{ width: '100%', padding: '4px 8px', borderRadius: 6, border: '1px solid #7339CE' }}
              />
              <input
                type="date"
                value={fechaVenceTemp}
                onChange={(e) => setFechaVenceTemp(e.target.value)}
                style={{ width: '100%', padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
              />
              <button
                type="button"
                onClick={handleGuardarTope}
                style={{ background: '#5E2AB8', color: '#fff', border: 0, borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 700 }}
              >
                Guardar
              </button>
            </div>
          ) : (
            <>
              <div className={estilos.kpiValor}>{formatearMoneda(topeContrato)}</div>
              <div className={estilos.kpiSubtexto}>
                Vence: <b>{proyecto.fecha_vencimiento_contrato || proyecto.fecha_fin}</b>
              </div>
            </>
          )}
        </div>

        {/* Devengado Real Ejecutado */}
        <div className={estilos.kpiCard}>
          <div className={estilos.kpiTitulo}>
            <span>Ejecutado Real (Devengado)</span>
            <IconoCartera style={{ width: '16px', height: '16px', color: '#5E2AB8' }} />
          </div>
          <div className={estilos.kpiValor} style={{ color: '#5E2AB8' }}>
            {formatearMoneda(valorDevengado)}
          </div>
          <div className={estilos.kpiSubtexto}>
            Avance físico: <b>{avancePonderado}%</b> · {totalModulos} actividades
          </div>
        </div>

        {/* Saldo Contractual Disponible */}
        <div className={`${estilos.kpiCard} ${saldoContractual < 0 ? estilos.kpiCardPeligro : pctConsumoTope > 85 ? estilos.kpiCardAlerta : ''}`}>
          <div className={estilos.kpiTitulo}>
            <span>Margen Contractual Restante</span>
            <IconoGrafico style={{ width: '16px', height: '16px' }} />
          </div>
          <div className={estilos.kpiValor} style={{ color: saldoContractual < 0 ? '#ef4444' : '#16a34a' }}>
            {formatearMoneda(saldoContractual)}
          </div>
          <div className={estilos.kpiSubtexto}>
            Consumo: <b>{pctConsumoTope}%</b> del tope aprobado
          </div>
        </div>

        {/* Plazo Contractual y Días Restantes */}
        <div className={`${estilos.kpiCard} ${diasRestantes <= 15 ? estilos.kpiCardPeligro : ''}`}>
          <div className={estilos.kpiTitulo}>
            <span>Tiempo de Contrato</span>
            <IconoReloj style={{ width: '16px', height: '16px' }} />
          </div>
          <div className={estilos.kpiValor}>
            {diasRestantes} días <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>restantes</span>
          </div>
          <div className={estilos.kpiSubtexto}>
            Transcurrido: {pctTiempoTranscurrido}% · SPI: <b>{spi}</b>
          </div>
          <div className={estilos.progresoTiempoBarra}>
            <div
              className={estilos.progresoTiempoFill}
              style={{
                width: `${pctTiempoTranscurrido}%`,
                background: pctTiempoTranscurrido > 90 ? '#ef4444' : '#5E2AB8',
              }}
            />
          </div>
        </div>
      </section>

      {/* 2. Gráfico Interactivo de Curva S */}
      <section className={estilos.seccionGrafico}>
        <div className={estilos.headerGrafico}>
          <div className={estilos.headerGraficoTitulos}>
            <h3>Curva S de Avance y Costos de Obra</h3>
            <p>Comparativo dinámico: Curva Contractual Programada vs Avance Físico Devengado vs Salidas de Bodega</p>
          </div>
          <div className={estilos.leyendaGrafico}>
            <div className={estilos.itemLeyenda}>
              <span className={estilos.lineaMuestraPlaneada} />
              <span>Planeado Contractual</span>
            </div>
            <div className={estilos.itemLeyenda}>
              <span className={estilos.lineaMuestraReal} />
              <span>Real Devengado ({avancePonderado}%)</span>
            </div>
            <div className={estilos.itemLeyenda}>
              <span style={{ width: 14, height: 3, backgroundColor: '#059669', display: 'inline-block' }} />
              <span>Lo Cobrado en Caja ({pctCobradoPonderado}%)</span>
            </div>
            <div className={estilos.itemLeyenda}>
              <span className={estilos.lineaMuestraCostos} />
              <span>Lo Gastado (Costos)</span>
            </div>
          </div>
        </div>

        {/* Alerta de Liquidez Contractual (Tarea 2.10) */}
        {desfaseLiquidez > 0 && avancePonderado > pctCobradoPonderado && (
          <div
            style={{
              margin: '0 0 12px',
              padding: '10px 14px',
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 8,
              color: '#92400e',
              fontSize: '12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <IconoAlerta style={{ width: 16, height: 16, color: '#d97706' }} />
            <span>
              <strong>Alerta de Liquidez (Tarea 2.10):</strong> La obra se está ejecutando ({avancePonderado}%) más rápido de lo que se cobra ({pctCobradoPonderado}%). Desfase en caja: <strong>{formatearMoneda(desfaseLiquidez)}</strong>.
            </span>
          </div>
        )}

        <div className={estilos.svgContenedor}>
          <svg viewBox={`0 0 ${viewBoxW} ${viewBoxH}`} className={estilos.svgGrafico}>
            {/* Título del Eje Y */}
            <text
              x={paddingX - 10}
              y={paddingY - 14}
              textAnchor="start"
              fontSize="10.5"
              fill="#334155"
              fontWeight="800"
            >
              ▲ % Avance &amp; Caja
            </text>

            {/* Cuadrícula horizontal porcentual */}
            {[0, 25, 50, 75, 100].map((pct) => {
              const y = coordY(pct)
              return (
                <g key={pct}>
                  <line
                    x1={paddingX}
                    y1={y}
                    x2={viewBoxW - paddingX}
                    y2={y}
                    stroke="#e2e8f0"
                    strokeDasharray={pct === 0 || pct === 100 ? undefined : '3,3'}
                  />
                  <text
                    x={paddingX - 10}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="11"
                    fill="#64748b"
                    fontWeight="600"
                  >
                    {pct}%
                  </text>
                </g>
              )
            })}

            {/* Curva S Planeada (Azul) */}
            <path
              d={dPlaneada}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="2.5"
              strokeDasharray="5,4"
            />

            {/* Curva de Costos Reales (Ámbar) */}
            {dCostos && (
              <path
                d={dCostos}
                fill="none"
                stroke="#f59e0b"
                strokeWidth="2.5"
              />
            )}

            {/* Curva de Lo Cobrado en Caja (Verde Esmeralda - Tarea 2.10) */}
            {dCobrado && (
              <path
                d={dCobrado}
                fill="none"
                stroke="#059669"
                strokeWidth="3"
              />
            )}

            {/* Puntos Cobrados */}
            {coordsCobrados.map((p, idx) => (
              <circle
                key={`cob-${idx}`}
                cx={p.x}
                cy={p.y}
                r="3.5"
                fill="#059669"
                stroke="#ffffff"
                strokeWidth="1.5"
              />
            ))}

            {/* Curva S Real Devengada (Morado Mega) */}
            {dReal && (
              <path
                d={dReal}
                fill="none"
                stroke="#5E2AB8"
                strokeWidth="3.5"
              />
            )}

            {/* Puntos y Vértices Reales */}
            {coordsReales.map((p, idx) => (
              <circle
                key={`real-${idx}`}
                cx={p.x}
                cy={p.y}
                r={idx === coordsReales.length - 1 ? 5.5 : 4}
                fill="#5E2AB8"
                stroke="#ffffff"
                strokeWidth="2"
              />
            ))}

            {/* Ticks y Porcentajes del Eje X */}
            {[0, 20, 40, 60, 80, 100].map((pct) => {
              const x = paddingX + (pct / 100) * chartW
              return (
                <g key={pct}>
                  <line
                    x1={x}
                    y1={paddingY + chartH}
                    x2={x}
                    y2={paddingY + chartH + 5}
                    stroke="#cbd5e1"
                  />
                  <text
                    x={x}
                    y={paddingY + chartH + 18}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#64748b"
                    fontWeight="700"
                  >
                    {pct}%
                  </text>
                </g>
              )
            })}

            {/* Título del Eje X */}
            <text
              x={paddingX + chartW / 2}
              y={viewBoxH - 8}
              textAnchor="middle"
              fontSize="10.5"
              fill="#334155"
              fontWeight="700"
            >
              Tiempo Contractual Transcurrido (% Plazo de Obra) ▶
            </text>

            {/* Indicador Exacto de HOY (Corte continuo y vértice coincidente) */}
            <line
              x1={xHoy}
              y1={paddingY}
              x2={xHoy}
              y2={paddingY + chartH}
              stroke="#ef4444"
              strokeWidth="1.8"
              strokeDasharray="4,2"
            />
            <text
              x={xHoy}
              y={paddingY - 8}
              textAnchor="middle"
              fontSize="10"
              fill="#ef4444"
              fontWeight="800"
            >
              HOY ({pctTiempoTranscurrido}%)
            </text>
          </svg>
        </div>
      </section>

      {/* 3. Tablas de Control: Horas Hombre y Salidas vs Presupuesto */}
      <section className={estilos.gridTablasControl}>
        {/* Horas Hombre: Estimadas vs Programadas vs Ejecutadas */}
        <div className={estilos.cardTablaControl}>
          <div className={estilos.cardTablaHeader}>
            <h4>
              <IconoReloj style={{ width: '17px', height: '17px', color: '#5E2AB8' }} />
              Control de Rendimiento (Horas Hombre)
            </h4>
            <span className={horasEjecutadasTotales > horasProgramadasTotales ? estilos.badgeAlertaStock : estilos.badgeStockOk}>
              {horasEjecutadasTotales - horasProgramadasTotales > 0
                ? `+${horasEjecutadasTotales - horasProgramadasTotales} h desviación`
                : `${horasProgramadasTotales - horasEjecutadasTotales} h disponibles`}
            </span>
          </div>

          <div className={estilos.tablaScroll}>
            <table className={estilos.tablaControl}>
              <thead>
                <tr>
                  <th>Actividad</th>
                  <th>Prog.</th>
                  <th>Ejec.</th>
                  <th>Var.</th>
                </tr>
              </thead>
              <tbody>
                {(proyecto.etapas || []).flatMap((e) => e.modulos || []).map((m) => {
                  const prog = Number(m.horas_programadas) > 0 ? Number(m.horas_programadas) : (Number(m.horas) || 0)
                  const ejec = Number(m.horas_ejecutadas) > 0 ? Number(m.horas_ejecutadas) : (Number(m.horas_reales) || 0)
                  const diff = ejec - prog
                  return (
                    <tr key={m.id}>
                      <td>
                        <b>{m.codigo}</b> - {m.nombre.slice(0, 36)}
                      </td>
                      <td>{Math.round(prog)} h</td>
                      <td>{Math.round(ejec)} h</td>
                      <td className={diff > 0 ? estilos.desviacionPositiva : diff < 0 ? estilos.desviacionNegativa : estilos.desviacionCero}>
                        {diff > 0 ? `+${Math.round(diff)}` : diff < 0 ? `${Math.round(diff)}` : '0'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Desviación de Salidas de Almacén y Alertas de Stock */}
        <div className={estilos.cardTablaControl}>
          <div className={estilos.cardTablaHeader}>
            <h4>
              <IconoCaja style={{ width: '17px', height: '17px', color: '#5E2AB8' }} />
              Alertas de Stock y Salidas de Bodega
            </h4>
            <span className={faltantesStock.length > 0 ? estilos.badgeAlertaStock : estilos.badgeStockOk}>
              {faltantesStock.length > 0 ? `${faltantesStock.length} faltantes requeridos` : 'Stock al día'}
            </span>
          </div>

          <div className={estilos.tablaScroll}>
            <table className={estilos.tablaControl}>
              <thead>
                <tr>
                  <th className={estilos.colModulo}>Módulo</th>
                  <th>Insumo / Material</th>
                  <th className={estilos.colRequerido}>Requerido</th>
                  <th className={estilos.colEstado}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {faltantesStock.length > 0 ? (
                  faltantesStock.map((f, i) => (
                    <tr key={i}>
                      <td className={estilos.colModulo}><b>{f.moduloCodigo}</b></td>
                      <td>{f.material}</td>
                      <td className={estilos.colRequerido}>{f.requerido} {f.unidad}</td>
                      <td className={estilos.colEstado}>
                        <span className={estilos.badgeAlertaStock}>
                          <IconoAlerta style={{ width: '12px', height: '12px', verticalAlign: 'middle', marginRight: 3 }} />
                          Falta en obra
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: '#64748b', padding: '16px' }}>
                      No hay solicitudes pendientes. Todos los materiales están despachados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {faltantesStock.length > 0 && (
            <button
              type="button"
              className={`${estilos.btnAlertarBodega} ${alertaEnviada ? estilos.btnAlertarBodegaOk : ''}`}
              onClick={handleNotificarBodega}
            >
              {alertaEnviada ? (
                <>
                  <IconoCheck style={{ width: '15px', height: '15px' }} />
                  ¡Notificación enviada a Bodega / Alistamientos!
                </>
              ) : (
                <>
                  <IconoCaja style={{ width: '15px', height: '15px' }} />
                  Alertar requerimiento urgente a Bodega ({faltantesStock.length} ítems)
                </>
              )}
            </button>
          )}
        </div>
      </section>
    </div>
  )
}
